import {Text, TextField} from '@/components/atoms'
import {usePromotion} from '@/context/Promotion/PromotionContext'
import {PromotionCondition, PromotionType, RewardType} from '@/types/promotion'
import {FC} from 'react'
import {useRewardValidations} from './RewardValidations.js'

type DiscountInputProps = {
  promotionCondition: PromotionCondition
  textBeforeInput?: string
  textAfterInput?: string
  showDynamicPlaceholder?: boolean
  discountType?: string
  index?: number
}

export const RewardDiscount: FC<DiscountInputProps> = ({
  promotionCondition,
  textBeforeInput = '$',
  textAfterInput = '% off.',
  showDynamicPlaceholder = false,
  discountType,
}) => {
  const {updateCondition, promotionConfig, promotion} = usePromotion()
  const maxDiscountPercentage = promotionConfig?.maxDiscountPercentage || 0

  const {rewards} = promotionCondition
  const currentReward = rewards?.[0]
  const isDiscountReward = currentReward?.type === RewardType.DISCOUNT
  const isCashDiscount = currentReward?.discountType === 'AMOUNT'
  const currentRewardDiscountPercentage = isDiscountReward ? currentReward.discountValue : 0
  const { hasValueLayerError } = useRewardValidations(promotionCondition)

  const updatePercentage = (value: string) => {
    const resolvedDiscountType = discountType ?? currentReward?.discountType ?? ''
    
    updateCondition({
      ...promotionCondition,
      rewards: [
        {
          type: RewardType.DISCOUNT,
          discountType: resolvedDiscountType,
          discountValue: Number(value),
        },
      ],
    })
  }

  const handleDiscountChange = value => {
    if (value === '') {
      updatePercentage(value)
      return
    }

    const decimalPart = value.split('.')[1]
    if (decimalPart && decimalPart.length > 4) {
      return
    }

    updatePercentage(value)
  }

  const placeholderText = (showDynamicPlaceholder && !isCashDiscount) ? `Up to ${maxDiscountPercentage}%` : '0.0'
  const discountPercentage = currentRewardDiscountPercentage || ''

  // Don't show error for STORE_DISCOUNT and WELCOME_DISCOUNT promotion types
  const isError =
    promotion.promotionType !== PromotionType.STORE_DISCOUNT &&
    promotion.promotionType !== PromotionType.WELCOME_DISCOUNT &&
    discountType !== 'AMOUNT' &&
    discountPercentage > maxDiscountPercentage
  
  const isLayerError =  hasValueLayerError()
  const className = isError ? 'disable-error-icon' : ''
  const errorText = isLayerError ? 'Value Invalid' : (isError ? ' ' : '')

  return (
    <>
      {isCashDiscount && <Text>{textBeforeInput}</Text>}
      <TextField
        updateValue={handleDiscountChange}
        value={discountPercentage.toString().replace(',', '.')}
        error={errorText}
        uiSchema={{
                  helperText: '',
                  placeholder: placeholderText,
                  className: className
                }}
        // @ts-ignore
        style={{minWidth: '160px'}}
        type='number'
      />
      {!isCashDiscount && <Text>{textAfterInput}</Text>}
    </>
  )
}
